// Pure distance and area math for the plan-drawing readout. No Leaflet, so
// it runs in jest and can serve any other surface that wants a length.

export interface LatLng {
  lat: number;
  lng: number;
}

/** Mean Earth radius in meters (the value haversine and Leaflet use). */
const EARTH_RADIUS_M = 6371008.8;
const METERS_PER_FOOT = 0.3048;
const FEET_PER_MILE = 5280;
const SQ_METERS_PER_ACRE = 4046.8564224;
// A length under this many feet reads in feet; at or above it, in miles.
const FEET_LIMIT = 1000;

const toRad = (deg: number): number => (deg * Math.PI) / 180;

/** Great-circle distance between two points, in meters. */
export function haversineMeters(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Total length of an open path, in meters. Fewer than 2 points is 0. */
export function pathLengthMeters(points: LatLng[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) total += haversineMeters(points[i - 1], points[i]);
  return total;
}

/**
 * Area of a ring on the sphere, in square meters. The ring closes itself
 * (last point back to the first). Same formula as Leaflet.draw's
 * geodesicArea, so the figure matches what other map tools show. Winding
 * order does not matter; the result is never negative. Fewer than 3 points
 * is 0.
 */
export function ringAreaSquareMeters(points: LatLng[]): number {
  const n = points.length;
  if (n < 3) return 0;
  let sum = 0;
  for (let i = 0; i < n; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % n];
    sum += toRad(p2.lng - p1.lng) * (2 + Math.sin(toRad(p1.lat)) + Math.sin(toRad(p2.lat)));
  }
  return Math.abs((sum * EARTH_RADIUS_M * EARTH_RADIUS_M) / 2);
}

/** "640 ft" under 1,000 ft, "1.2 mi" from there up. */
export function formatLength(meters: number): string {
  const feet = meters / METERS_PER_FOOT;
  if (Math.round(feet) < FEET_LIMIT) return `${Math.round(feet)} ft`;
  return `${(feet / FEET_PER_MILE).toFixed(1)} mi`;
}

/** "3.4 acres", or "<0.1 acres" when the area rounds to nothing. */
export function formatArea(squareMeters: number): string {
  const acres = squareMeters / SQ_METERS_PER_ACRE;
  if (acres < 0.05) return '<0.1 acres';
  return `${acres.toFixed(1)} acres`;
}
